/**
 * EditorCard — Single editor persona card
 *
 * Always clickable, no disabled state.
 * Shows: emoji, name, description, Run button, score badge, run count.
 */

import { Play, RotateCcw, Loader2, CheckCircle } from 'lucide-react'
import type { EditorTypeKey, EditorResultData } from '@/lib/api'

interface EditorCardProps {
  editorKey: EditorTypeKey
  displayName: string
  emoji: string
  description: string
  suggestedOrder: number
  result: EditorResultData | null
  isRunning: boolean
  onRun: () => void
  onViewResult: () => void
}

function getSeverityColor(score: number): string {
  if (score >= 8) return 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30'
  if (score >= 6) return 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/30'
  if (score >= 4) return 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-900/30'
  return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
}

export function EditorCard({
  editorKey: _,
  displayName,
  emoji,
  description,
  suggestedOrder,
  result,
  isRunning,
  onRun,
  onViewResult,
}: EditorCardProps) {
  const hasResult = !!result
  const isAccepted = hasResult && !!result.acceptedAt

  return (
    <div
      className={`rounded-lg border p-3 transition-all hover:shadow-md cursor-pointer ${
        isRunning
          ? 'border-indigo-300 bg-indigo-50/50 dark:border-indigo-500/50 dark:bg-indigo-900/20'
          : isAccepted
            ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-700/50 dark:bg-emerald-900/10'
            : hasResult
              ? 'border-amber-200 bg-white dark:border-amber-800/50 dark:bg-surface-800'
              : 'border-gray-200 bg-white dark:border-white/10 dark:bg-surface-800'
      }`}
      onClick={hasResult && !isRunning ? onViewResult : undefined}
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{emoji}</span>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{suggestedOrder}</span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                {displayName}
              </h3>
            </div>
          </div>
        </div>

        {/* Score badge + accepted indicator */}
        {hasResult && (
          <div className="flex items-center gap-1.5">
            {isAccepted && (
              <CheckCircle className="w-4 h-4 text-emerald-500" />
            )}
            <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${getSeverityColor(result.score)}`}>
              {result.score}/10
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
        {description}
      </p>

      {/* Footer: status + action button */}
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-gray-400 dark:text-gray-500">
          {isAccepted ? (
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
              {result.acceptedCount === result.suggestions.length
                ? `All ${result.suggestions.length} accepted`
                : `${result.acceptedCount} of ${result.suggestions.length} applied`
              }
            </span>
          ) : hasResult ? (
            <>
              {result.suggestions.length} suggestion{result.suggestions.length !== 1 ? 's' : ''} pending
            </>
          ) : (
            'Not run yet'
          )}
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onRun() }}
          disabled={isRunning}
          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            isRunning
              ? 'bg-indigo-100 text-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-400 cursor-wait'
              : hasResult
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/20'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {isRunning ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Running...
            </>
          ) : hasResult ? (
            <>
              <RotateCcw className="w-3 h-3" />
              Re-run
            </>
          ) : (
            <>
              <Play className="w-3 h-3" />
              Run
            </>
          )}
        </button>
      </div>
    </div>
  )
}
